import React, { useState, useContext, useRef } from 'react';
import { AppContext, getStorageQuotaByPosition } from '../../context/AppContext';
import { validateUserIdentityUniqueness } from '../../utils/userValidation';
import { 
  UserPlus, Search, UserCheck, UserX, Forward, Shield, ShieldAlert, 
  Key, Lock, Edit3, Building, Briefcase, Phone, Mail, HardDrive, 
  Download, UploadCloud, FileSpreadsheet, CheckCircle, AlertCircle, X, ShieldCheck
} from 'lucide-react';

const UserManagement = () => {
  const { 
    users, 
    groupMails,
    addEmployee, 
    batchImportEmployees, 
    updateEmployeeInfo, 
    updateEmployeeRole, 
    currentUser,
    terminateEmployee,
    restoreEmployee,
    retirementHistory,
    getUserStorageUsage
  } = useContext(AppContext);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [deptFilter, setDeptFilter] = useState('ALL');
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'retired'
  
  // Modals state
  const [isRegistering, setIsRegistering] = useState(false);
  const [isBatchImporting, setIsBatchImporting] = useState(false);
  const [isManagingAccount, setIsManagingAccount] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Terminate employee state (Requirement 1)
  const [isTerminating, setIsTerminating] = useState(false);
  const [terminatingUser, setTerminatingUser] = useState(null);
  const [terminateReason, setTerminateReason] = useState('개인 사유');
  const [terminateSuccessor, setTerminateSuccessor] = useState('');

  // Batch import CSV file ref and parsed preview
  const fileInputRef = useRef(null);
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [csvRawText, setCsvRawText] = useState('');

  // New employee individual form state (Default initial password: clou1234)
  const [newName, setNewName] = useState('');
  const [newAccount, setNewAccount] = useState('');
  const [newPhone, setNewPhone] = useState('010-');
  const [newDept, setNewDept] = useState('클라우드네이티브');
  const [newJob, setNewJob] = useState('사원');
  const [newDuty, setNewDuty] = useState('-');
  const [newEmpType, setNewEmpType] = useState('정규');
  const [newEmpStatus, setNewEmpStatus] = useState('재직');
  const [newRole, setNewRole] = useState('STAFF');
  const [newJoinDate, setNewJoinDate] = useState(new Date().toISOString().split('T')[0]);

  // Account manage & Edit form state
  const [editName, setEditName] = useState('');
  const [editAccount, setEditAccount] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editDept, setEditDept] = useState('클라우드네이티브');
  const [editJob, setEditJob] = useState('사원');
  const [editDuty, setEditDuty] = useState('-');
  const [editEmpType, setEditEmpType] = useState('정규');
  const [editEmpStatus, setEditEmpStatus] = useState('재직');
  const [editRole, setEditRole] = useState('STAFF');
  const [editQuota, setEditQuota] = useState('10');
  const [accountStatus, setAccountStatus] = useState('Active');
  const [forwardingEmail, setForwardingEmail] = useState('');
  const [userAliases, setUserAliases] = useState('');
  const [editJoinDate, setEditJoinDate] = useState('');

  const calculateTenure = (joinDateStr) => {
    if (!joinDateStr || joinDateStr === '-') return '-';
    try {
      const joinDate = new Date(joinDateStr);
      if (isNaN(joinDate.getTime())) return '-';
      const now = new Date();
      let years = now.getFullYear() - joinDate.getFullYear();
      let months = now.getMonth() - joinDate.getMonth();
      if (months < 0) {
        years--;
        months += 12;
      }
      return `${years}년 ${months}개월`;
    } catch (e) {
      return '-';
    }
  };

  const handleOpenManageModal = (user) => {
    setSelectedUser(user);
    setEditName(user.Name || '');
    setEditAccount(user.Account || user.Email?.split('@')[0] || '');
    setEditPhone(user.Phone || '');
    setEditDept(user.Department || '클라우드네이티브');
    setEditJob(user.JobTitle || user.Position || '사원');
    setEditDuty(user.Duty || '-');
    setEditEmpType(user.EmploymentType || '정규');
    setEditEmpStatus(user.EmploymentStatus || '재직');
    setEditRole(user.Role || 'STAFF');
    setEditQuota(String(user.StorageQuota || 10));
    setAccountStatus(user.Status || 'Active');
    setForwardingEmail(user.ForwardingEmail || '');
    setUserAliases(user.Aliases || '');
    setEditJoinDate(user.JoinDate || '');
    setIsManagingAccount(true);
  };

  // 1. Single Employee Register
  const handleRegister = (e) => {
    e.preventDefault();
    if (!newName || !newAccount) {
      alert('직원 성명과 계정 아이디를 입력해 주세요.');
      return;
    }

    const cleanAccount = newAccount.trim().replace('@clouflake.com', '');
    const cleanEmail = `${cleanAccount}@clouflake.com`;

    const valResult = validateUserIdentityUniqueness(null, cleanAccount, cleanEmail, '', users, groupMails);
    if (!valResult.isValid) {
      alert(`[계정 중복 검사 실패]\n${valResult.error}`);
      return;
    }

    addEmployee({
      Name: newName.trim(),
      Account: cleanAccount,
      Email: cleanEmail,
      Phone: newPhone.trim(),
      Department: newDept,
      JobTitle: newJob,
      Position: newJob,
      Duty: newDuty,
      EmploymentType: newEmpType,
      EmploymentStatus: newEmpStatus,
      Role: newRole,
      JoinDate: newJoinDate
    });

    // Reset
    setNewName('');
    setNewAccount('');
    setNewPhone('010-');
    setIsRegistering(false);
    alert(`[${newName}] 직원이 등록되었습니다. 초기 비밀번호는 'clou1234'이며, 첫 로그인 시 비밀번호 변경 화면이 자동으로 실행됩니다.`);
  };

  // 2. Edit Employee Submit
  const handleManageSubmit = (e) => {
    e.preventDefault();
    if (selectedUser) {
      const cleanAccount = (editAccount.trim() || selectedUser.Account).replace('@clouflake.com', '');
      const cleanEmail = `${cleanAccount}@clouflake.com`;

      const valResult = validateUserIdentityUniqueness(
        selectedUser.UserID,
        cleanAccount,
        cleanEmail,
        userAliases,
        users,
        groupMails
      );

      if (!valResult.isValid) {
        alert(`[정보 수정 실패]\n${valResult.error}`);
        return;
      }

      if (valResult.selfFilteredNotice) {
        alert(`[안내] ${valResult.selfFilteredNotice}`);
      }

      updateEmployeeInfo(selectedUser.UserID, {
        Name: editName.trim() || selectedUser.Name,
        Account: cleanAccount,
        Phone: editPhone.trim(),
        Department: editDept,
        JobTitle: editJob,
        Position: editJob,
        Duty: editDuty,
        EmploymentType: editEmpType,
        EmploymentStatus: editEmpStatus,
        Role: editRole,
        StorageQuota: Number(editQuota),
        Status: accountStatus,
        ForwardingEmail: forwardingEmail.trim(),
        Aliases: valResult.cleanedAliasesStr,
        JoinDate: editJoinDate,
        Tenure: calculateTenure(editJoinDate)
      });
      setIsManagingAccount(false);
      setSelectedUser(null);
      alert(`[${editName}] 직원의 인사 정보 및 권한(${editRole})이 수정되었습니다.`);
    }
  };

  // Action: Reset Password to default 'clou1234' and force change
  const handleResetPassword = (user) => {
    if (!user) return;
    if (confirm(`[${user.Name}] 직원의 비밀번호를 초기 비밀번호 'clou1234'로 초기화하시겠습니까?\n초기화된 사용자는 다음 로그인 시 비밀번호를 필수로 변경해야 합니다.`)) {
      updateEmployeeInfo(user.UserID, { Password: 'clou1234', MustChangePassword: true });
      alert(`[${user.Name}] 직원의 비밀번호가 'clou1234'로 성공적으로 초기화되었습니다.`);
      setIsManagingAccount(false); // Close edit modal if open
    }
  };

  // 3. Quick Role Change (Admin Assign/Revoke)
  const handleToggleAdminRole = (user) => {
    const isTargetAdmin = user.Role === 'ADMIN' || user.Account === 'clou0315' || user.Account === 'chris';
    const nextRole = isTargetAdmin ? 'STAFF' : 'ADMIN';

    // Prevent revoking if only 1 admin remains
    const adminCount = users.filter(u => u.Role === 'ADMIN').length;
    if (isTargetAdmin && adminCount <= 1) {
      alert('시스템에 최소 1명 이상의 관리자가 존재해야 합니다.');
      return;
    }

    const actionText = nextRole === 'ADMIN' ? '관리자(ADMIN) 권한을 부여' : '관리자 권한을 해지(일반 직원 전환)';
    if (confirm(`[${user.Name}] 님에게 ${actionText}하시겠습니까?`)) {
      updateEmployeeRole(user.UserID, nextRole);
      alert(`[${user.Name}] 님의 권한이 ${nextRole}로 변경되었습니다.`);
    }
  };

  // 3-1. Terminate Employee Action (Requirement 1)
  const handleOpenTerminateModal = (user) => {
    if (user.UserID === currentUser.UserID) {
      alert('현재 로그인된 관리자 본인은 퇴사 처리할 수 없습니다.');
      return;
    }
    setTerminatingUser(user);
    setTerminateReason('개인 사유');
    setTerminateSuccessor('');
    setIsTerminating(true);
  };

  const handleConfirmTerminate = (e) => {
    e.preventDefault();
    if (!terminatingUser) return;
    
    if (confirm(`정말로 [${terminatingUser.Name}] 직원을 퇴사 처리(삭제)하시겠습니까?`)) {
      terminateEmployee(terminatingUser.UserID, terminateReason, terminateSuccessor);
      alert(`[${terminatingUser.Name}] 직원이 퇴사 처리되었습니다. 퇴직 사원 관리 탭에서 확인 및 복원할 수 있습니다.`);
      setIsTerminating(false);
      setTerminatingUser(null);
    }
  };

  // 3-2. Restore Employee Action (Requirement 1)
  const handleRestore = (user) => {
    const targetDept = prompt('복원할 직원의 부서를 입력해주세요:', '클라우드네이티브') || '클라우드네이티브';
    const targetJob = prompt('복원할 직원의 직위/직급을 입력해주세요:', '사원') || '사원';

    if (confirm(`[${user.Name}] 직원을 다시 재직 상태로 복원하시겠습니까?`)) {
      restoreEmployee(user.UserID, targetDept, targetJob);
      alert(`[${user.Name}] 직원이 성공적으로 재직 상태로 복원되었습니다. 직급 기준 스토리지(${getStorageQuotaByPosition(targetJob)}GB)가 새로 배정되었습니다.`);
    }
  };

  // 4. Excel / CSV Export (Requirement 2)
  const handleExportExcel = () => {
    const headers = ['이름', '계정', '직원구분', '재직 상태', '직위', '직책', '사용자 그룹', '부서명', '입사일', '재직기간', '계정 상태', '관리자권한', '수신별칭'];
    const rows = users.map(u => [
      u.Name,
      u.Account || u.Email.split('@')[0],
      u.EmploymentType || '정규',
      u.EmploymentStatus || (u.Status === 'Retired' ? '퇴사' : '재직'),
      u.Position || u.JobTitle || '사원',
      u.Duty || '-',
      u.UserGroup || '-',
      u.Department || '클라우드네이티브',
      u.JoinDate || '-',
      u.Tenure || '-',
      u.Status === 'Active' ? '정상' : (u.Status === 'Suspended' ? '중지' : '퇴사'),
      u.Role === 'ADMIN' ? '관리자' : '일반',
      u.Aliases || ''
    ]);

    const csvContent = '\uFEFF' + [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `clouflake_employees_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 5. Batch Import CSV File Parser
  const parseCSVText = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(line => line.trim().length > 0);
    if (lines.length <= 1) return [];

    const headers = lines[0].split(',').map(h => h.replace(/^["'\s\uFEFF]+|["'\s]+$/g, ''));
    const parsedList = [];

    for (let i = 1; i < lines.length; i++) {
      // Regex for CSV with quoted values
      const matches = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || lines[i].split(',');
      const row = {};
      headers.forEach((h, idx) => {
        let val = matches[idx] || '';
        val = val.replace(/^["']|["']$/g, '').trim();
        row[h] = val;
      });
      if (row['이름'] || row['계정']) {
        parsedList.push(row);
      }
    }
    return parsedList;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setCsvRawText(content);
      const parsed = parseCSVText(content);
      setCsvPreviewData(parsed);
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleExecuteBatchImport = () => {
    let listToImport = csvPreviewData;
    if (listToImport.length === 0 && csvRawText.trim()) {
      listToImport = parseCSVText(csvRawText);
    }

    if (listToImport.length === 0) {
      alert('일괄 등록할 유효한 사원 데이터가 없습니다. CSV 파일 또는 텍스트를 확인해 주세요.');
      return;
    }

    const count = batchImportEmployees(listToImport);
    alert(`총 ${count}명의 직원이 일괄 등록되었습니다.\n모든 직원의 초기 비밀번호는 'clou1234'로 설정되었습니다.`);
    setIsBatchImporting(false);
    setCsvPreviewData([]);
    setCsvRawText('');
  };

  // Filtered users list
  const filteredUsers = users.filter(user => {
    const isRetired = user.Status === 'Retired' || user.EmploymentStatus === '퇴사';
    if (activeTab === 'active' && isRetired) return false;
    if (activeTab === 'retired' && !isRetired) return false;

    const matchSearch = (
      user.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Account?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.JobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Aliases?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchDept = deptFilter === 'ALL' || user.Department === deptFilter;
    return matchSearch && matchDept;
  });

  return (
    <div className="space-y-6 text-left">
      {/* Top Action Bar: Search, Dept Filter, Excel Export, Batch Import, Add User */}
      <div className="flex flex-col xl:flex-row justify-between items-stretch xl:items-center gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
        <div className="flex flex-col sm:flex-row items-center gap-3 flex-1">
          {/* Search Box */}
          <div className="relative w-full sm:max-w-xs">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="이름, 계정, 부서, 직급, 별칭 검색..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-600 focus:bg-white text-slate-900 placeholder-slate-400"
            />
          </div>

          {/* Department Filter */}
          <select
            value={deptFilter}
            onChange={e => setDeptFilter(e.target.value)}
            className="w-full sm:w-auto px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs md:text-sm font-bold text-slate-700 focus:outline-none focus:border-indigo-600"
          >
            <option value="ALL">전체 부서</option>
            <option value="클라우드네이티브">클라우드네이티브</option>
            <option value="AI서비스개발">AI서비스개발</option>
            <option value="경영마케팅">경영마케팅</option>
            <option value="경영지원팀">경영지원팀</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Excel Export Button (Requirement 2) */}
          <button
            onClick={handleExportExcel}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-2.5 px-4 rounded-xl border border-slate-200 transition-all cursor-pointer text-xs md:text-sm shadow-2xs"
            title="현재 직원 목록을 엑셀(CSV) 파일로 다운로드합니다"
          >
            <Download className="w-4 h-4 text-emerald-600" />
            <span>엑셀 다운로드</span>
          </button>

          {/* Batch Import Button (Requirement 2) */}
          <button
            onClick={() => setIsBatchImporting(true)}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold py-2.5 px-4 rounded-xl border border-emerald-200 transition-all cursor-pointer text-xs md:text-sm shadow-2xs"
            title="엑셀/CSV 양식을 업로드하여 직원을 일괄 등록합니다"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span>직원 일괄 등록</span>
          </button>

          {/* Single Register Button */}
          <button
            onClick={() => setIsRegistering(true)}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl shadow-sm transition-all cursor-pointer text-xs md:text-sm"
          >
            <UserPlus className="w-4 h-4" />
            <span>신규 사원 등록</span>
          </button>
        </div>
      </div>

      {/* Tab Switcher (Requirement 1) */}
      <div className="flex border-b border-slate-200 gap-1 bg-slate-50/50 p-1.5 rounded-2xl border">
        <button
          onClick={() => setActiveTab('active')}
          className={`flex-1 sm:flex-none px-6 py-2.5 font-bold text-xs md:text-sm rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'active'
              ? 'bg-white text-indigo-700 shadow-xs border border-slate-200'
              : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100/50'
          }`}
        >
          <UserCheck className="w-4 h-4 text-emerald-600" />
          <span>재직 사원 명부 ({users.filter(u => u.Status !== 'Retired' && u.EmploymentStatus !== '퇴사').length}명)</span>
        </button>
        <button
          onClick={() => setActiveTab('retired')}
          className={`flex-1 sm:flex-none px-6 py-2.5 font-bold text-xs md:text-sm rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'retired'
              ? 'bg-white text-indigo-700 shadow-xs border border-slate-200'
              : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100/50'
          }`}
        >
          <UserX className="w-4 h-4 text-rose-600" />
          <span>퇴사자 명고 및 복원 히스토리 ({users.filter(u => u.Status === 'Retired' || u.EmploymentStatus === '퇴사').length}명)</span>
        </button>
      </div>

      {/* Users Data Table with All Real Fields */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xs overflow-hidden">
        <div className="p-4 md:p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <h3 className="text-base md:text-lg font-bold text-slate-900">
              {activeTab === 'active' 
                ? `클로우플레이크 재직 직원 명단 (${filteredUsers.length}명)` 
                : `클로우플레이크 퇴직 사원 히스토리 명부 (${filteredUsers.length}명)`}
            </h3>
            <p className="text-xs md:text-sm text-slate-500 mt-0.5">
              {activeTab === 'active' 
                ? '재직 사원의 소속 부서, 직위, 직책, 별칭 및 스토리지 공간(직급별 차등)을 제어합니다.'
                : '퇴사 처리된 직원 정보와 퇴사 사유 및 인수인계 계정 히스토리를 보관하며, 언제든지 복원할 수 있습니다.'}
            </p>
          </div>
          {activeTab === 'active' && (
            <span className="text-xs px-3 py-1 bg-indigo-50 text-indigo-700 font-bold rounded-lg border border-indigo-100">
              초기 비밀번호: clou1234
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          {activeTab === 'active' ? (
            <table className="w-full text-left text-sm text-slate-800">
              <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3.5 whitespace-nowrap">이름</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">계정 (ID)</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">부서명</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">직위 / 직책</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">스토리지 용량</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">재직 상태</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">입사일 (재직기간)</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">별칭 (Aliases)</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">권한 (Role)</th>
                  <th className="px-4 py-3.5 text-right whitespace-nowrap">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.map(user => {
                  const isAdmin = user.Role === 'ADMIN' || user.Account === 'clou0315' || user.Account === 'chris';

                  return (
                    <tr key={user.UserID} className="hover:bg-slate-50/80 transition-colors">
                      {/* Name */}
                      <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-xs">
                          {user.Name.charAt(0)}
                        </div>
                        <span>{user.Name}</span>
                      </td>

                      {/* Account ID / Email */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <div className="font-mono text-xs font-bold text-slate-900">{user.Account || user.Email.split('@')[0]}</div>
                        <span className="text-[11px] text-slate-400 font-mono">{user.Email}</span>
                      </td>

                      {/* Department */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                          {user.Department || '-'}
                        </span>
                      </td>

                      {/* Position / Duty */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span className="font-bold text-indigo-700 text-xs">
                          {user.Position || user.JobTitle || '사원'}
                        </span>
                        {user.Duty && user.Duty !== '-' && (
                          <span className="ml-1 text-[11px] text-slate-500 font-medium">({user.Duty})</span>
                        )}
                      </td>

                      {/* Storage Quota (Rank formula: 2GB Base + 2GB per level higher) */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <HardDrive className="w-4 h-4 text-indigo-500" />
                          <span className="font-bold text-slate-900">{user.StorageQuota || 2} GB</span>
                          <span className="text-[10px] text-slate-400">
                            ({getUserStorageUsage ? `${getUserStorageUsage(user.UserID).usedMB}MB` : '0MB'} 사용)
                          </span>
                        </div>
                      </td>

                      {/* Employment Status */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-100 text-emerald-800">
                          재직
                        </span>
                      </td>

                      {/* Join Date & Tenure */}
                      <td className="px-4 py-3.5 whitespace-nowrap text-xs">
                        <span className="text-slate-700 font-medium">{user.JoinDate || '-'}</span>
                        {user.Tenure && user.Tenure !== '-' && (
                          <span className="text-slate-400 block text-[11px]">{user.Tenure}</span>
                        )}
                      </td>

                      {/* Aliases */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {user.Aliases ? (
                          <span className="px-2 py-0.5 rounded-md text-xs font-mono font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            {user.Aliases}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>

                      {/* Role (ADMIN / STAFF) with 1-click Toggle */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleAdminRole(user)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer transition-all ${
                            isAdmin
                              ? 'bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 shadow-2xs'
                              : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                          }`}
                          title={isAdmin ? '클릭 시 관리자 권한 해지' : '클릭 시 관리자 권한 부여'}
                        >
                          {isAdmin ? <ShieldCheck className="w-3.5 h-3.5 text-amber-700" /> : <Shield className="w-3.5 h-3.5 text-slate-400" />}
                          <span>{isAdmin ? '관리자 (ADMIN)' : '일반 (STAFF)'}</span>
                        </button>
                      </td>
                      {/* Action Buttons */}
                      <td className="px-4 py-3.5 text-right whitespace-nowrap space-x-1.5">
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                          title="비밀번호 초기화 (clou1234, 첫 로그인 시 변경 유도)"
                        >
                          <Key className="w-3.5 h-3.5" />
                          <span>초기화</span>
                        </button>
                        <button
                          onClick={() => handleOpenManageModal(user)}
                          className="px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                          title="직원 정보 상세 수정"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          <span>수정</span>
                        </button>
                        <button
                          onClick={() => handleOpenTerminateModal(user)}
                          className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                          title="사원 퇴사 처리 (복원 및 이력 조회 가능)"
                        >
                          <UserX className="w-3.5 h-3.5" />
                          <span>퇴사</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-left text-sm text-slate-800">
              <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3.5 whitespace-nowrap">퇴사 사원</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">퇴사 계정</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">퇴사 당시 직급</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">퇴사 일자</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">퇴사 사유</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">인수인계 / 이메일 포워딩</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">상태</th>
                  <th className="px-4 py-3.5 text-right whitespace-nowrap">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-slate-400 text-xs md:text-sm font-medium">
                      퇴사 처리된 사원이 존재하지 않습니다.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(user => {
                    const record = retirementHistory.find(h => h.UserID === user.UserID) || {
                      RetiredAt: user.JoinDate || '2026-08-15',
                      Reason: '개인 사정',
                      Successor: user.ForwardingEmail || '미지정'
                    };

                    return (
                      <tr key={user.UserID} className="hover:bg-slate-50/80 transition-colors bg-rose-50/10">
                        {/* Retired Name */}
                        <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-rose-100 text-rose-700 font-bold flex items-center justify-center text-xs">
                            {user.Name.charAt(0)}
                          </div>
                          <span className="line-through text-slate-500">{user.Name}</span>
                        </td>

                        {/* Account ID / Email */}
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <div className="font-mono text-xs text-slate-500 font-bold">{user.Account}</div>
                          <span className="text-[11px] text-slate-400 font-mono">{user.Email}</span>
                        </td>

                        {/* Position */}
                        <td className="px-4 py-3.5 whitespace-nowrap text-slate-600 text-xs font-medium">
                          {user.Position || user.JobTitle || '사원'}
                        </td>

                        {/* Termination Date */}
                        <td className="px-4 py-3.5 whitespace-nowrap text-slate-700 font-bold text-xs">
                          {record.RetiredAt}
                        </td>

                        {/* Retirement Reason */}
                        <td className="px-4 py-3.5 text-xs text-slate-800 font-semibold max-w-xs truncate">
                          {record.Reason}
                        </td>

                        {/* Successor Email / Forwarding */}
                        <td className="px-4 py-3.5 whitespace-nowrap text-xs">
                          {record.Successor ? (
                            <span className="px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-mono">
                              {record.Successor}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>

                        {/* Status Label */}
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-rose-100 text-rose-800">
                            퇴사 완료
                          </span>
                        </td>

                        {/* Restoration Action */}
                        <td className="px-4 py-3.5 text-right whitespace-nowrap">
                          <button
                            onClick={() => handleRestore(user)}
                            className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                            title="재직 상태로 다시 복원하며 스토리지를 재배정합니다"
                          >
                            <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
                            <span>재직 복원</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* MODAL 1: Individual Register Modal (Default password: clou1234) */}
      {isRegistering && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 text-left">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-600" />
                <h2 className="text-base font-bold text-slate-900">신규 사원 계정 등록</h2>
              </div>
              <button onClick={() => setIsRegistering(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleRegister} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">이름 *</label>
                  <input
                    type="text"
                    placeholder="예: 홍길동"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">계정 ID (메일 아이디) *</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="gdhong"
                      value={newAccount}
                      onChange={e => setNewAccount(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900 font-mono"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-mono">@clouflake.com</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">부서명</label>
                  <select
                    value={newDept}
                    onChange={e => setNewDept(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  >
                    <option value="클라우드네이티브">클라우드네이티브</option>
                    <option value="AI서비스개발">AI서비스개발</option>
                    <option value="경영마케팅">경영마케팅</option>
                    <option value="경영지원팀">경영지원팀</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">직위 (Job Title)</label>
                  <select
                    value={newJob}
                    onChange={e => setNewJob(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  >
                    <option value="사원">사원</option>
                    <option value="전임">전임</option>
                    <option value="선임">선임</option>
                    <option value="책임">책임</option>
                    <option value="수석">수석</option>
                    <option value="팀장">팀장</option>
                    <option value="이사">이사</option>
                    <option value="대표이사">대표이사</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">직무 권한 (Role)</label>
                  <select
                    value={newRole}
                    onChange={e => setNewRole(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  >
                    <option value="STAFF">일반 직원 (STAFF)</option>
                    <option value="ADMIN">관리자 (ADMIN)</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">입사일</label>
                  <input
                    type="date"
                    value={newJoinDate}
                    onChange={e => setNewJoinDate(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  />
                </div>
              </div>

              <div className="p-4 bg-indigo-50/70 border border-indigo-100 rounded-2xl space-y-1 text-xs text-indigo-900">
                <div className="flex items-center gap-1.5 font-bold">
                  <Key className="w-4 h-4 text-indigo-600" />
                  <span>초기 임시 비밀번호: <code className="bg-white px-2 py-0.5 rounded border border-indigo-200 font-mono text-indigo-700">clou1234</code></span>
                </div>
                <p className="text-indigo-700 text-[11px] leading-relaxed">
                  등록된 사원이 첫 로그인할 때 본인만의 안전한 비밀번호로 즉시 재설정하도록 강제됩니다.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsRegistering(false)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer"
                >
                  등록 완료
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Batch CSV Import Modal (Requirement 2) */}
      {isBatchImporting && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 text-left flex flex-col max-h-[90vh]">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                <h2 className="text-base font-bold text-slate-900">직원 명부 엑셀 / CSV 일괄 등록</h2>
              </div>
              <button onClick={() => setIsBatchImporting(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1 text-sm text-slate-700">
              <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-2xl space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-emerald-900 text-xs uppercase tracking-wider">
                    📋 표준 CSV 템플릿 양식 안내
                  </span>
                  <button
                    onClick={handleExportExcel}
                    className="text-xs font-bold text-emerald-700 hover:underline flex items-center gap-1"
                  >
                    <Download className="w-3 h-3" />
                    현재 양식 다운로드
                  </button>
                </div>
                <p className="text-xs text-emerald-800">
                  헤더 형식: <code>이름,계정,직원구분,재직 상태,직위,직책,사용자 그룹,부서명,입사일,재직기간,계정 상태</code>
                </p>
              </div>

              {/* Upload Input */}
              <div className="space-y-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".csv,text/csv,text/plain"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-slate-300 hover:border-indigo-400 p-6 rounded-2xl text-center space-y-2 bg-slate-50/50 hover:bg-indigo-50/20 cursor-pointer transition-all"
                >
                  <UploadCloud className="w-8 h-8 text-indigo-600 mx-auto" />
                  <div className="font-bold text-slate-800 text-sm">PC에서 CSV 파일 선택하기</div>
                  <div className="text-xs text-slate-400">또는 아래에 CSV 텍스트를 직접 붙여넣으셔도 됩니다.</div>
                </button>
              </div>

              {/* Raw CSV Text Paste */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">CSV 텍스트 직접 입력 / 붙여넣기</label>
                <textarea
                  rows={4}
                  placeholder={`이름,계정,직원구분,재직 상태,직위,직책,사용자 그룹,부서명,입사일,재직기간,계정 상태\n홍길동,gdhong,정규,재직,사원,-,-,클라우드네이티브,2026-01-01,0년 1개월,정상`}
                  value={csvRawText}
                  onChange={e => {
                    setCsvRawText(e.target.value);
                    setCsvPreviewData(parseCSVText(e.target.value));
                  }}
                  className="w-full p-3 bg-white border border-slate-300 rounded-xl font-mono text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                />
              </div>

              {/* Parsed Preview Table */}
              {csvPreviewData.length > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-indigo-700">
                      인식된 사원 데이터 미리보기 ({csvPreviewData.length}명)
                    </span>
                    <span className="text-xs text-slate-400">초기 비밀번호: clou1234</span>
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-left text-xs text-slate-700">
                      <thead className="bg-slate-100 font-bold text-slate-600 sticky top-0">
                        <tr>
                          <th className="px-3 py-2">이름</th>
                          <th className="px-3 py-2">계정</th>
                          <th className="px-3 py-2">부서</th>
                          <th className="px-3 py-2">직위</th>
                          <th className="px-3 py-2">상태</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {csvPreviewData.map((row, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-1.5 font-bold text-slate-900">{row.이름 || row.Name}</td>
                            <td className="px-3 py-1.5 font-mono">{row.계정 || row.Account}</td>
                            <td className="px-3 py-1.5">{row.부서명 || row.Department}</td>
                            <td className="px-3 py-1.5">{row.직위 || row.Position}</td>
                            <td className="px-3 py-1.5">{row['재직 상태'] || '재직'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
              <span className="text-xs text-slate-500 font-medium">
                {csvPreviewData.length > 0 ? `${csvPreviewData.length}명의 사원 일괄 등록 준비 완료` : '데이터를 업로드해 주세요.'}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsBatchImporting(false)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold rounded-xl"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleExecuteBatchImport}
                  disabled={csvPreviewData.length === 0}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span>일괄 등록 실행</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: Detailed Employee Edit Modal */}
      {isManagingAccount && selectedUser && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 text-left">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h2 className="text-base font-bold text-slate-900">
                  {selectedUser.Name} ({selectedUser.Account || selectedUser.Email.split('@')[0]}) 인사 정보 수정
                </h2>
                <span className="text-xs text-slate-400 font-mono">{selectedUser.Email}</span>
              </div>
              <button onClick={() => setIsManagingAccount(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleManageSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">성명</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">연락처</label>
                  <input
                    type="text"
                    value={editPhone}
                    onChange={e => setEditPhone(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">소속 부서</label>
                  <select
                    value={editDept}
                    onChange={e => setEditDept(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  >
                    <option value="클라우드네이티브">클라우드네이티브</option>
                    <option value="AI서비스개발">AI서비스개발</option>
                    <option value="경영마케팅">경영마케팅</option>
                    <option value="경영지원팀">경영지원팀</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">직위</label>
                  <select
                    value={editJob}
                    onChange={e => setEditJob(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  >
                    <option value="사원">사원</option>
                    <option value="전임">전임</option>
                    <option value="선임">선임</option>
                    <option value="책임">책임</option>
                    <option value="수석">수석</option>
                    <option value="팀장">팀장</option>
                    <option value="이사">이사</option>
                    <option value="대표이사">대표이사</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">관리자 권한 (Role)</label>
                  <select
                    value={editRole}
                    onChange={e => setEditRole(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900 font-bold"
                  >
                    <option value="STAFF">일반 직원 (STAFF)</option>
                    <option value="ADMIN">관리자 (ADMIN)</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">입사 일자</label>
                  <input
                    type="date"
                    value={editJoinDate}
                    onChange={e => setEditJoinDate(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900 font-medium"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase">재직 상태</label>
                  <select
                    value={editEmpStatus}
                    onChange={e => setEditEmpStatus(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  >
                    <option value="재직">재직</option>
                    <option value="휴직">휴직</option>
                    <option value="퇴사">퇴사</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">메일 수신 별칭 (Aliases)</label>
                <input
                  type="text"
                  placeholder="예: anderson@clouflake.com"
                  value={userAliases}
                  onChange={e => setUserAliases(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-mono focus:outline-none focus:border-indigo-600 text-slate-900"
                />
                <span className="text-[11px] text-slate-400 block">
                  * 쉼표(,)로 구분하여 여러 별칭 주소를 등록할 수 있습니다.
                </span>
              </div>

              <div className="flex justify-between items-center pt-2">
                <button
                  type="button"
                  onClick={() => handleResetPassword(selectedUser)}
                  className="px-4 py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 font-bold text-xs rounded-xl cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                >
                  <Key className="w-3.5 h-3.5" />
                  <span>비밀번호 'clou1234'로 초기화</span>
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsManagingAccount(false)}
                    className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl cursor-pointer"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer"
                  >
                    수정 사항 저장
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: Terminate Employee (Retire) Modal (Requirement 1) */}
      {isTerminating && terminatingUser && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 text-left">
            <div className="bg-rose-50 px-6 py-4 border-b border-rose-100 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-rose-600" />
                <h2 className="text-base font-bold text-rose-950">사원 퇴사 처리 (인사 삭제)</h2>
              </div>
              <button 
                onClick={() => {
                  setIsTerminating(false);
                  setTerminatingUser(null);
                }} 
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleConfirmTerminate} className="p-6 space-y-4">
              <div className="bg-rose-50/50 border border-rose-100 p-3 rounded-xl space-y-1">
                <p className="text-xs font-bold text-rose-900">퇴사 대상자 정보</p>
                <div className="text-sm font-semibold text-slate-800">
                  {terminatingUser.Name} ({terminatingUser.Position || terminatingUser.JobTitle})
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  {terminatingUser.Email}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">퇴사 사유 *</label>
                <select
                  value={terminateReason}
                  onChange={e => setTerminateReason(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-rose-500 text-slate-900"
                  required
                >
                  <option value="개인 사유">개인 사유 (일반 퇴직)</option>
                  <option value="이직">이직 (전직)</option>
                  <option value="정년퇴직">정년퇴직</option>
                  <option value="계약 만료">계약 만료</option>
                  <option value="기타">기타 (직접 입력)</option>
                </select>
              </div>

              {terminateReason === '기타' && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-150">
                  <input
                    type="text"
                    placeholder="상세 퇴사 사유를 입력하세요"
                    value={terminateReason === '기타' ? '' : terminateReason}
                    onChange={e => setTerminateReason(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-rose-500 text-slate-900"
                    required
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">인수인계 계정 (포워딩 이메일)</label>
                <input
                  type="email"
                  placeholder="예: anderson@clouflake.com"
                  value={terminateSuccessor}
                  onChange={e => setTerminateSuccessor(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-rose-500 text-slate-900 font-mono"
                />
                <span className="text-[10px] text-slate-400 block leading-normal">
                  * 지정 시 퇴사 사원의 수발신 메일이 인수인계 계정으로 포워딩 설정됩니다.
                </span>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsTerminating(false);
                    setTerminatingUser(null);
                  }}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer"
                >
                  퇴사(삭제) 완료
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
